import React from 'react'
import type { UseAvatarPopoverProps } from './useAvatarPopover.types'

/**
 * Manages the accessibility behavior for avatar popovers, ensuring they are keyboard navigable
 *
 * @see https://procoretech.atlassian.net/browse/UXI-1632?focusedCommentId=6513946
 */
export const useAvatarPopover = (props: UseAvatarPopoverProps) => {
  const { focusableSelector, overlayWrapperSelector } = props
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const openedByKeyboardRef = React.useRef(false)

  const popoverContentRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (node && openedByKeyboardRef.current) {
        const firstFocusable = node.querySelector(
          focusableSelector
        ) as HTMLElement | null
        firstFocusable?.focus()
      }
    },
    [focusableSelector]
  )

  const beforeShow = React.useCallback((event: Event) => {
    // Block auto-open on keyboard focus alone - require Enter/Space
    if (event.type === 'focusin') {
      return false
    }
    openedByKeyboardRef.current = event instanceof KeyboardEvent
    return true
  }, [])

  const beforeHide = React.useCallback(
    (event: Event) => {
      if (event.type === 'focusout') {
        const relatedTarget = (event as FocusEvent)
          .relatedTarget as HTMLElement | null
        // Keep open when focus moves into the overlay (e.g. auto-focus after keyboard open)
        if (relatedTarget?.closest(overlayWrapperSelector)) {
          return false
        }
        // For hover-opened popovers, don't close on focus changes
        if (!openedByKeyboardRef.current) {
          return false
        }
      }
      return true
    },
    [overlayWrapperSelector]
  )

  const afterHide = React.useCallback(() => {
    if (openedByKeyboardRef.current) {
      requestAnimationFrame(() => triggerRef.current?.focus())
    }
    openedByKeyboardRef.current = false
  }, [])

  return {
    triggerRef,
    popoverContentRef,
    beforeShow,
    beforeHide,
    afterHide,
  }
}
